import { useReducer, Reducer, useEffect } from 'react';
import * as api from '../config/api';
import { convertFromString } from 'utils/date';

export type UserState = {
  tag: 'LOADED';
  birthday: Date | null;
  isHiddenAsset: {
    value: boolean;
    toggleAssetVisibilityState: SetAPIState;
  };
  isHiddenAssetD: {
    value: boolean;
    toggleAssetVisibilityState: SetAPIState;
  };
  isLinkedLine: boolean;
  isAuthenticatedMoneytree: boolean | null;
  isLinkedMoneytree: boolean;
};
type Loading = {
  tag: 'IS_LOADING';
  birthday: null;
  isHiddenAsset: {
    value: null;
    toggleAssetVisibilityState: null;
  };
  isHiddenAssetD: {
    value: null;
    toggleAssetVisibilityState: null;
  };
  isLinkedLine: boolean;
  isAuthenticatedMoneytree: boolean | null;
  isLinkedMoneytree: boolean | null;
};
export type User = UserState | Loading;

const loading: Loading = {
  tag: 'IS_LOADING',
  birthday: null,
  isHiddenAsset: {
    toggleAssetVisibilityState: null,
    value: null,
  },
  isHiddenAssetD: {
    toggleAssetVisibilityState: null,
    value: null,
  },
  isLinkedLine: false,
  isAuthenticatedMoneytree: null,
  isLinkedMoneytree: null,
};

type SetAPIState = 'IS_READY' | 'SETTING_COOLTIME' | 'IS_COOLTIME';

type UseHomeUser = {
  homeUser: User;
  getHomeUser: () => void;
  toggleAssetVisibility: () => void;
};

type Action =
  | { type: 'toggleAssetVisibility' }
  | { type: 'coolTimeStarted' }
  | { type: 'coolTimeEnded' }
  | {
      type: 'gotMainUser';
      payload: {
        birthday: Date | null;
        isHiddenAsset: boolean;
        isHiddenAssetD: boolean;
        isLinkedLine: boolean;
        isAuthenticatedMoneytree: boolean | null;
        isLinkedMoneytree: boolean;
      };
    };

const reducer = (state: User, action: Action): User => {
  switch (action.type) {
    case 'toggleAssetVisibility':
      if (isLoading(state)) return state;
      if (state.isHiddenAsset.toggleAssetVisibilityState !== 'IS_READY')
        return state;
      if (state.isHiddenAssetD.toggleAssetVisibilityState !== 'IS_READY')
        return state;
      return {
        ...state,
        isHiddenAsset: {
          value: !state.isHiddenAsset.value,
          toggleAssetVisibilityState: 'SETTING_COOLTIME',
        },
        isHiddenAssetD: {
          value: !state.isHiddenAssetD.value,
          toggleAssetVisibilityState: 'SETTING_COOLTIME',
        },
      };
    case 'coolTimeStarted':
      if (isLoading(state)) return state;
      return {
        ...state,
        isHiddenAsset: {
          ...state.isHiddenAsset,
          toggleAssetVisibilityState: 'IS_COOLTIME',
        },
        isHiddenAssetD: {
          ...state.isHiddenAssetD,
          toggleAssetVisibilityState: 'IS_COOLTIME',
        },
      };
    case 'coolTimeEnded':
      if (isLoading(state)) return loading;
      return {
        ...state,
        isHiddenAsset: {
          ...state.isHiddenAsset,
          toggleAssetVisibilityState: 'IS_READY',
        },
        isHiddenAssetD: {
          ...state.isHiddenAssetD,
          toggleAssetVisibilityState: 'IS_READY',
        },
      };
    case 'gotMainUser':
      return {
        tag: 'LOADED',
        birthday: action.payload.birthday,
        isHiddenAsset: {
          value: action.payload.isHiddenAsset,
          toggleAssetVisibilityState: 'IS_READY',
        },
        isHiddenAssetD: {
          value: action.payload.isHiddenAssetD,
          toggleAssetVisibilityState: 'IS_READY',
        },
        isLinkedLine: action.payload.isLinkedLine,
        isAuthenticatedMoneytree: action.payload.isAuthenticatedMoneytree,
        isLinkedMoneytree: action.payload.isLinkedMoneytree,
      };
  }
};

export const useHomeUser = (apiCoolTimeMilliseconds: number): UseHomeUser => {
  const [homeUser, dispatch] = useReducer<Reducer<User, Action>>(
    reducer,
    loading
  );

  const getHomeUser = async (): Promise<void> => {
    const result = await api.getUser();
    dispatch({
      type: 'gotMainUser',
      payload: {
        birthday: result.Birthday
          ? convertFromString(result.Birthday, true)
          : null,
        isHiddenAsset: result.HideAsset,
        isHiddenAssetD: result.HideAssetD,
        isLinkedLine: result.LinkedLine,
        isAuthenticatedMoneytree: result.AuthenticatedMoneytree,
        isLinkedMoneytree: result.LinkedMoneytree,
      },
    });
  };
  const toggleAssetVisibility = async (): Promise<void> => {
    dispatch({
      type: 'toggleAssetVisibility',
    });
    if (isLoading(homeUser)) return;
    if (homeUser.isHiddenAsset.toggleAssetVisibilityState === 'IS_READY')
      api.updateUser({ HideAsset: !homeUser.isHiddenAsset.value });
    if (homeUser.isHiddenAssetD.toggleAssetVisibilityState === 'IS_READY')
      api.updateUser({ HideAssetD: !homeUser.isHiddenAssetD.value });
  };

  useEffect(() => {
    if (isLoading(homeUser)) return;
    if (
      homeUser.isHiddenAsset.toggleAssetVisibilityState ===
        'SETTING_COOLTIME' ||
      homeUser.isHiddenAssetD.toggleAssetVisibilityState === 'SETTING_COOLTIME'
    ) {
      setTimeout(() => {
        dispatch({ type: 'coolTimeEnded' });
      }, apiCoolTimeMilliseconds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeUser]);

  return {
    homeUser,
    getHomeUser,
    toggleAssetVisibility,
  };
};

export const isLoading = (state: User): state is Loading => {
  return state.tag === 'IS_LOADING';
};
